import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { FC, ReactNode } from 'react';

import { ProviderCallContext } from './ProviderCallContext';
import { useProviderCallState } from './useProviderCallState';

interface ProviderCallContextControllerProps {
  /**
   * The Daily.co room ID
   */
  roomId: string;

  /**
   * The Healthie appointment data
   */
  appointment: AppointmentResponse | undefined;

  /**
   * Whether the current user is a provider
   */
  isProvider: boolean;

  /**
   * Child components that can access the provider call context
   */
  children: ReactNode;
}

/**
 * Context provider for provider call state management.
 *
 * This component must be rendered:
 * - INSIDE DailyCallProvider (to access Daily hooks like useParticipantIds)
 * - ABOVE components that need call state (ProviderCallContent, DebugTools, etc.)
 *
 * Provides centralized state management for:
 * - Appointment creation when both participants join
 * - Duration calculation and manual editing
 * - Time limit notifications
 *
 * State is organized into three logical groups:
 * - lifecycle: Appointment creation and participant tracking
 * - duration: Time calculation and manual editing
 * - ui: Modal state and user interactions
 *
 * @example
 * ```typescript
 * <DailyCallProvider>
 *   <ProviderCallContextController
 *     roomId={roomId}
 *     appointment={appointment}
 *     isProvider={true}
 *   >
 *     <ProviderCallContent />
 *   </ProviderCallContextController>
 * </DailyCallProvider>
 * ```
 *
 * @see useProviderCallContext - Hook to consume this context
 * @see useProviderCallState - Internal hook containing business logic
 */
export const ProviderCallContextController: FC<
  ProviderCallContextControllerProps
> = ({ roomId, appointment, isProvider, children }) => {
  // All the logic from useOngoingAppointment, organized into context value
  const state = useProviderCallState({
    roomId,
    appointment,
    isProvider,
  });

  return (
    <ProviderCallContext.Provider value={state}>
      {children}
    </ProviderCallContext.Provider>
  );
};
